import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface AICostMeterProps {
  sessionCostUsd: number;
  inputTokens: number;
  outputTokens: number;
}

const AICostMeter: React.FC<AICostMeterProps> = ({
  sessionCostUsd,
  inputTokens,
  outputTokens,
}) => {
  const totalTokens = inputTokens + outputTokens;

  // Don't render if no AI usage yet
  if (totalTokens === 0) {
    return null;
  }

  const getCostColor = () => {
    if (sessionCostUsd >= 1.0) return '#ef4444'; // red
    if (sessionCostUsd >= 0.5) return '#fbbf24'; // yellow
    if (sessionCostUsd >= 0.1) return '#60a5fa'; // blue
    return '#10b981'; // green
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return tokens.toString();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-black/50 backdrop-blur-sm p-2 border border-gray-800/50 h-full flex flex-col shadow-lg"
      >
        <div className="flex items-center justify-between mb-0.5">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">AI Cost</h3>
          <div className="flex items-center space-x-1">
            <div className="w-1.5 h-1.5 bg-purple-500/60 animate-pulse" />
            <span className="text-[10px] text-purple-400">Bedrock</span>
          </div>
        </div>
        <p className="text-[10px] text-gray-600 mb-1">Session total</p>

        <div className="flex-1 flex flex-col justify-center space-y-1">
          {/* Session Cost */}
          <motion.div
            className="text-center py-2"
            animate={{
              backgroundColor:
                sessionCostUsd >= 0.5 ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-xs text-gray-500">Total</div>
            <motion.div
              key={sessionCostUsd.toFixed(4)}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              className="text-lg font-bold"
              style={{ color: getCostColor() }}
            >
              ${sessionCostUsd.toFixed(4)}
            </motion.div>
          </motion.div>

          {/* Token Breakdown */}
          <div className="border-t border-gray-800/50 pt-2">
            <div className="grid grid-cols-2 gap-1 text-center">
              <div>
                <div className="text-[10px] text-gray-500">Input</div>
                <div className="text-xs font-mono text-gray-400">
                  {formatTokens(inputTokens)}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">Output</div>
                <div className="text-xs font-mono text-gray-400">
                  {formatTokens(outputTokens)}
                </div>
              </div>
            </div>
          </div>

          {/* Total Tokens */}
          <div className="text-center">
            <div className="text-[10px] text-gray-500">Total Tokens</div>
            <div className="text-xs font-mono text-gray-400">
              {formatTokens(totalTokens)}
            </div>
          </div>

          {/* High cost warning */}
          {sessionCostUsd >= 0.5 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 pt-2 border-t border-gray-800/50"
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center justify-center space-x-1">
                <div className="h-1.5 w-1.5 bg-yellow-500/60 animate-pulse" />
                <span className="text-xs text-yellow-500/80">High AI usage</span>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AICostMeter;
